/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-07-30 05:42:06
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Page,
  Layout,
  Card,
  Button,
  ButtonGroup,
  DataTable,
  Link,
  Icon,
  Tooltip,
  TextField,
  SkeletonDisplayText,
  Pagination,
  DisplayText,
  TextStyle,
  Stack
} from '@shopify/polaris';
import {
  EditMajor,
  DeleteMajor,
  QuestionMarkMajor,
  SearchMinor,
  CirclePlusMajor,
  CircleMinusMajor
} from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import MainMenu from 'src/components/containers/main-menu';
import PopoverCloneButton from 'src/components/views/filter-tree/popover-clone-button';
import {
  NUMBER_DEFAULT_FILTER,
  NUMBER_FILTER_TREE_PER_PAGE,
  LIMIT_FILTER_OPTIONS
} from 'src/constants/filter-tree';
import { getShop, prettyText } from 'src/helpers';
import { collectFilterTreeStatus, collectionsExpandStatus } from 'src/helpers/filter-tree-helper';
import InfoBanner from 'src/components/views/filter-tree/info-banner';
import ConfirmRemoveModal from 'src/components/views/filter-tree/confirm-remove-modal';
import LimitCloneModal from 'src/components/views/filter-tree/limit-clone-modal';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import YoutubeModal from 'src/components/views/common/youtube-modal';

import { routes } from 'src/routes';
import history from 'src/routes/history';
import { URL } from 'src/constants/links';

import * as managementActions from 'actions/filter-tree/management-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import TitleHelp from 'src/components/views/common/title-help';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';

const renderRowsLoadingFilter = count => {
  let rows = [];
  for (var i = 0; i < count; i++) {
    rows.push([
      <SkeletonDisplayText size="small" />,
      <SkeletonDisplayText size="small" />,
      <SkeletonDisplayText size="small" />,
      <SkeletonDisplayText size="small" />
    ]);
  }

  return rows;
};

const renderCollectionsDefaultFilter = i18n => {
  return (
    <ul className="collections">
      <li>
        <span>
          {i18n.translate(
            'AdminHTML.FilterTree.Management.management_RenderCollectionsDefaultFilter_Span'
          )}
        </span>
        <span className="tooltip">
          <Tooltip
            content={
              <span className="tooltipContent">
                {i18n.translate(
                  'AdminHTML.FilterTree.Management.management_RenderCollectionsDefaultFilter_TooltipContent'
                )}
              </span>
            }
            preferredPosition="above"
            children={<span>text</span>}>
            <Icon source={QuestionMarkMajor} color="base" />
          </Tooltip>
        </span>
      </li>
    </ul>
  );
};

const renderCollectionsDefaultSearch = i18n => {
  return (
    <ul className="collections">
      <li>
        <span>
          {i18n.translate(
            'AdminHTML.FilterTree.Management.management_RenderCollectionsDefaultSearch_Span'
          )}
        </span>
        <span className="tooltip">
          <Tooltip
            content={i18n.translate(
              'AdminHTML.FilterTree.Management.management_RenderCollectionsDefaultSearch_TooltipContent'
            )}
            preferredPosition="above">
            <Icon source={QuestionMarkMajor} color="base" />
          </Tooltip>
        </span>
      </li>
    </ul>
  );
};

type Props = {
  // Trigger api status
  loading: boolean,
  searching: boolean,
  cloning: boolean,
  removing: boolean,
  updatingStatus: boolean,
  success: boolean,
  removedFilterId: string,

  // Filter trees
  defaultFilterTrees: Array<string>,
  searchFilterTrees: Array<string>,
  customFilterTrees: Array<string>,
  total: number,

  // Action
  listFilterTree: () => void,
  searchFilterTree: () => void,
  changeFilterTreeStatus: () => void,
  cloneFilterTree: () => void,
  removeFilterTree: () => void,
  getChecklist: () => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  firstPageLoading: boolean,
  inputSearchText: string,
  skip: number,
  limit: number,
  hasPrevious: boolean,
  hasNext: boolean,
  filterTreeStatus: Object,
  customFilterTrees: Array<Object>,
  collectionsExpand: Object,
  clonePosition: number,
  cloneTimes: number,
  activeModal: boolean,
  limitNumber: number,
  limitCloneActiveModal: boolean,
  activeInfoBanner: boolean,
  youtubeModal: boolean
};
// DONE
class ManageFilterTree extends Component<ComposedProps, State> {
  state = {
    firstPageLoading: true,
    inputSearchText: '',
    skip: 0,
    limit: NUMBER_DEFAULT_FILTER + NUMBER_FILTER_TREE_PER_PAGE,
    hasPrevious: false,
    hasNext: false,
    filterTreeStatus: {},
    customFilterTrees: [],
    collectionsExpand: {},
    clonePosition: -1,
    cloneTimes: 0,

    // Open/Close modal
    activeModal: false,

    // Open/Close limit clone modal
    limitNumber: LIMIT_FILTER_OPTIONS,
    limitCloneActiveModal: false,

    // Show info banner
    activeInfoBanner: true,
    youtubeModal: false
  };

  selectedFilterId: '';

  // Timer for typing on search field
  timerTyping = undefined;

  componentDidMount() {
    // Display info banner
    this.showInfoBanner();

    // List filter tree for first page, get 12 filter trees: 1 default filter, 1 search filter, 10 custom filter
    const { skip, limit } = this.state;
    this.props.listFilterTree('', skip, limit);
  }

  componentWillReceiveProps(nextProps) {
    const { skip, limit, firstPageLoading, clonePosition } = this.state;
    const {
      total,
      searchFilterTrees,
      customFilterTrees: customFilterTreesNextProps,
      clonedFilterTrees,
      updatingStatus,
      success,
      loading,
      cloning,
      removing,
      removedFilterId,
      searching
    } = nextProps;
    const {
      loading: previousLoading,
      cloning: previousCloning,
      removing: previousRemoving,
      searching: previousSearching
    } = this.props;

    const hasNext = skip + limit < total;
    const hasPrevious = skip > 0;

    if (firstPageLoading && !updatingStatus && !previousCloning && !previousRemoving) {
      // Save custom filter trees, filter tree status, pagination info to state
      let filterTreeStatus = collectFilterTreeStatus(searchFilterTrees, customFilterTreesNextProps);
      let collectionsExpand = collectionsExpandStatus(customFilterTreesNextProps);

      this.setState({
        hasNext,
        hasPrevious,
        filterTreeStatus,
        collectionsExpand,
        customFilterTrees: customFilterTreesNextProps
      });
    } else {
      let { customFilterTrees, filterTreeStatus, collectionsExpand, activeModal } = this.state;

      // Case list/search filter tree with pagination
      if (
        (previousLoading && !loading && success) ||
        (previousSearching && !searching && success)
      ) {
        customFilterTrees = customFilterTreesNextProps;
        filterTreeStatus = collectFilterTreeStatus(searchFilterTrees, customFilterTrees);
        collectionsExpand = collectionsExpandStatus(customFilterTrees);
      }

      // Case cloning filter tree
      if (clonePosition >= -1 && previousCloning && !cloning && success) {
        // Remove inserted skeleton element
        _.remove(customFilterTrees, filterTree => {
          return _.isEmpty(filterTree);
        });

        // Insert cloned filter trees from clone position
        let index = clonePosition + 1;
        _.forEach(clonedFilterTrees, filterTree => {
          const { filterId, status } = filterTree;
          customFilterTrees.splice(index++, 0, filterTree);
          filterTreeStatus[filterId] = status === 'active' ? true : false;
          collectionsExpand[filterId] = false;
        });
      }

      // Case remove filter tree
      // Remove filter tree from list of custom filter tree
      if (previousRemoving && !removing) {
        _.remove(customFilterTrees, { filterId: removedFilterId });
        activeModal = !activeModal;
      }

      this.setState({
        hasNext,
        hasPrevious,
        customFilterTrees,
        clonePosition: -1, // Reset clone position
        cloneTimes: 0, // Reset clone times,
        filterTreeStatus,
        collectionsExpand,
        activeModal // Reset confirmation for remove modal
      });
    }
  }

  render() {
    const { loading, defaultFilterTrees, searchFilterTrees, i18n } = this.props;
    const { firstPageLoading, customFilterTrees, hasPrevious, hasNext, youtubeModal, limitNumber } =
      this.state;

    let rowsDefaultFilter = renderRowsLoadingFilter(2);
    let rowsCustomFilter = renderRowsLoadingFilter(5);

    if (!loading || !firstPageLoading) {
      const defaultFilters = _.concat(defaultFilterTrees, searchFilterTrees);
      rowsDefaultFilter = this.buildRows(defaultFilters);
      rowsCustomFilter = this.buildRows(customFilterTrees);
    }

    return (
      <div className="BC-Page BC-Page__FilterTree-Management">
        <MainMenu menuId="menu-filters-filter-trees" />
        <Page
          title={i18n.translate(
            'AdminHTML.FilterTree.Management.management_ManageFilterTree_PageTitle'
          )}
          primaryAction={{
            content: i18n.translate(
              'AdminHTML.FilterTree.Management.management_ManageFilterTree_PagePrimaryAction_Content'
            ),
            onAction: () => {
              history.push(routes.filterTree.new());
            }
          }}>
          <TitleHelp
            i18n={i18n}
            onPlay={() => this.toggleModal('youtubeModal')}
            hrefUserManual={URL['Link_V2_Filter_ManageFilterTree_ManualGuide']}
            onAnalytics={() => history.push(routes.analytics.overviewCollection())}
          />
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Installation reminder status banner */}
                <InstallationRemindStatusBanner />

                {this.state.activeInfoBanner ? (
                  <InfoBanner onDismiss={this.handleDismissInfoBanner} />
                ) : (
                  <div className="BC-Page BC-Page__Text">
                    <DisplayText size="small">
                      <TextStyle variation="subdued">
                        {i18n.translate(
                          'AdminHTML.FilterTree.Management.management_ManageFilterTree_TextStyle'
                        )}
                      </TextStyle>
                    </DisplayText>
                  </div>
                )}

                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />
              </div>
            </Layout.Section>

            <Layout.Section>
              <Card
                title={i18n.translate(
                  'AdminHTML.FilterTree.Management.management_ManageFilterTree_CardTitle'
                )}
                sectioned>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text']}
                  headings={[
                    i18n.translate('AdminHTML.FilterTree.Common.status'),
                    i18n.translate(
                      'AdminHTML.FilterTree.Management.management_ManageFilterTree_DataTable_Heading2'
                    ),
                    i18n.translate('AdminHTML.FilterTree.Common.collections'),
                    i18n.translate('AdminHTML.FilterTree.Common.actions')
                  ]}
                  rows={rowsDefaultFilter}
                />
              </Card>
              <Card>
                <Card.Header
                  title={i18n.translate(
                    'AdminHTML.FilterTree.Management.management_ManageFilterTree_CardHeader_Title'
                  )}>
                  <div className="search" onKeyDown={this.handleKeyDown} onKeyUp={this.handleKeyUp}>
                    <TextField
                      onChange={this.onSearchFilterTrees}
                      value={this.state.inputSearchText}
                      prefix={<Icon source={SearchMinor} color="base" />}
                      placeholder={i18n.translate(
                        'AdminHTML.FilterTree.Management.management_ManageFilterTree_CardHeader_Placeholder'
                      )}
                    />
                  </div>
                </Card.Header>
                <Card.Section>
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text']}
                    headings={[
                      i18n.translate('AdminHTML.FilterTree.Common.status'),
                      i18n.translate(
                        'AdminHTML.FilterTree.Management.management_ManageFilterTree_DataTable_Heading2'
                      ),
                      i18n.translate('AdminHTML.FilterTree.Common.collections'),
                      i18n.translate('AdminHTML.FilterTree.Common.actions')
                    ]}
                    rows={rowsCustomFilter}
                  />
                </Card.Section>
                <Card.Section>
                  <Stack distribution="trailing">
                    <Button
                      primary
                      onClick={() => {
                        history.push(routes.filterTree.new());
                      }}>
                      {i18n.translate(
                        'AdminHTML.FilterTree.Management.management_ManageFilterTree_PagePrimaryAction_Content'
                      )}
                    </Button>
                  </Stack>
                </Card.Section>
              </Card>

              {(hasPrevious || hasNext) && (
                <div className="pagination">
                  <Pagination
                    hasPrevious={this.state.hasPrevious}
                    onPrevious={this.getPreviousPage}
                    hasNext={this.state.hasNext}
                    onNext={this.getNextPage}
                  />
                </div>
              )}
            </Layout.Section>
          </Layout>

          <YoutubeModal
            active={youtubeModal}
            embeddedURL={URL['Link_V2_Filter_ManageFilterTree_Video']}
            onClose={() => this.toggleModal('youtubeModal')}
            title={i18n.translate('AdminHTML.FilterTree.FilterTree.youtubeModal_Title')}
          />

          <ConfirmRemoveModal
            active={this.state.activeModal}
            removing={this.props.removing}
            handleChange={this.handleChangeModal}
            handleRemove={this.handleRemove}
            title={i18n.translate(
              'AdminHTML.FilterTree.Management.management_ManageFilterTree_ConfirmRemoveModal_Title'
            )}
            content={i18n.translate(
              'AdminHTML.FilterTree.Management.management_ManageFilterTree_ConfirmRemoveModal_Content'
            )}
          />
          <LimitCloneModal
            active={this.state.limitCloneActiveModal}
            handleChange={this.handleChangeLimitCloneModal}
            numberOfFilterOptions={limitNumber}
          />
        </Page>
      </div>
    );
  }

  buildRows(filterTrees, isDefault = true) {
    const { i18n } = this.props;
    let rows = [];
    _.forEach(filterTrees, filterTree => {
      const { filterId, name, collections, isDefault, isDefaultSearch, status } = filterTree;

      if (filterId) {
        rows.push([
          isDefault ? '' : this.renderCheckbox(filterId, status),
          <Link
            removeUnderline
            onClick={() => {
              history.push(routes.filterTree.view(filterId));
            }}>
            {name}
          </Link>,
          isDefault
            ? renderCollectionsDefaultFilter(i18n)
            : isDefaultSearch
            ? renderCollectionsDefaultSearch(i18n)
            : this.renderCollections(filterId, collections),
          isDefault || isDefaultSearch
            ? this.renderActionsDefaultFilter(filterId, isDefault ? 'default' : 'search')
            : this.renderActionsCustomFilter(filterId, 'custom')
        ]);
      } else {
        rows.push([
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />
        ]);
      }
    });

    return rows;
  }

  renderCheckbox(filterId, status) {
    return (
      <label className="BC-Checkbox-Status">
        <input
          type="checkbox"
          checked={this.state.filterTreeStatus[filterId]}
          onChange={() => this.handleChangeStatus(filterId)}
        />
        <span />
      </label>
    );
  }

  renderCollections = (filterId, collections) => {
    const { collectionsExpand } = this.state;
    const { i18n } = this.props;
    const viewMore = _.isArray(collections) ? collections.length > 7 : false;
    const expand = collectionsExpand[filterId];

    let subCollections = collections || [];
    if (viewMore && !expand) {
      subCollections = collections.slice(0, 7);
    }

    return (
      <div>
        <ul className="collections">
          {subCollections.map(collection => {
            const { id, title } = collection;
            return title ? (
              <li key={id}>
                <span>{title}</span>
              </li>
            ) : (
              ''
            );
          })}
        </ul>
        <span className="collectionsMore">
          {viewMore ? (
            expand ? (
              <Link onClick={() => this.handleCollectionsExpand(filterId)}>
                <Icon source={CircleMinusMajor} color="base" />
                <span className="text">
                  {i18n.translate(
                    'AdminHTML.FilterTree.Management.management_ManageFilterTree_RenderCollections_Span_Less'
                  )}
                </span>
              </Link>
            ) : (
              <Link onClick={() => this.handleCollectionsExpand(filterId)}>
                <Icon source={CirclePlusMajor} color="base" />
                <span className="text">
                  {i18n.translate(
                    'AdminHTML.FilterTree.Management.management_ManageFilterTree_RenderCollections_Span_More'
                  )}
                </span>
              </Link>
            )
          ) : (
            ''
          )}
        </span>
      </div>
    );
  };

  // Handle view more/less for collections
  handleCollectionsExpand = filterId => {
    const { collectionsExpand } = this.state;
    collectionsExpand[filterId] = !collectionsExpand[filterId];

    this.setState({ collectionsExpand });
  };

  renderActionsDefaultFilter(filterId, type) {
    return (
      <ButtonGroup segmented>
        <Button
          size="slim"
          icon={EditMajor}
          onClick={() => history.push(routes.filterTree.view(filterId))}
        />
        <PopoverCloneButton
          filterId={filterId}
          handleClone={(id, cloneTimes) => this.handleClone(id, cloneTimes, type)}
        />
      </ButtonGroup>
    );
  }

  renderActionsCustomFilter(filterId, type) {
    return (
      <ButtonGroup segmented>
        <Button
          size="slim"
          icon={EditMajor}
          onClick={() => history.push(routes.filterTree.view(filterId))}
        />
        <PopoverCloneButton
          filterId={filterId}
          handleClone={(id, cloneTimes) => this.handleClone(id, cloneTimes, type)}
        />
        <Button size="slim" icon={DeleteMajor} onClick={() => this.handleChangeModal(filterId)} />
      </ButtonGroup>
    );
  }

  // Search filter
  onSearchFilterTrees = inputSearchText => {
    this.setState({ inputSearchText });
  };

  // Key press event on search field
  handleKeyDown = event => {
    clearTimeout(this.timerTyping);
  };

  // Key up event, start searching after 500ms
  handleKeyUp = event => {
    const charList = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const key = event.key.toLowerCase();
    const keyCode = event.keyCode;

    if (charList.indexOf(key) === -1 && keyCode !== 8) {
      return;
    }

    // Invoke search request after 500ms
    this.timerTyping = setTimeout(() => {
      this.handleSearch();
    }, 500);
  };

  // List filter tree for next page
  getNextPage = () => {
    const { skip, limit, inputSearchText } = this.state;
    const { total } = this.props;
    const nextSkip = skip + limit;

    // Prevent multiple click that the skip number is bigger than total
    if (nextSkip >= total) return;

    // Set skip, limit for next page and disable flag for first page loading, don't need to display skeleton loading
    this.setState({ skip: nextSkip, limit: NUMBER_FILTER_TREE_PER_PAGE, firstPageLoading: false });
    this.props.listFilterTree(inputSearchText || '', nextSkip, NUMBER_FILTER_TREE_PER_PAGE);
  };

  // List filter tree for previous page
  getPreviousPage = () => {
    const { skip, limit, inputSearchText } = this.state;
    const totalFiltersFirstPage = NUMBER_DEFAULT_FILTER + NUMBER_FILTER_TREE_PER_PAGE;
    const previousSkip = skip - totalFiltersFirstPage === 0 ? 0 : skip - limit;
    const previousLimit = !previousSkip ? totalFiltersFirstPage : NUMBER_FILTER_TREE_PER_PAGE;

    // Prevent multiple click that the skip number is less than 0
    if (previousSkip < 0) return;

    // Set skip, limit for next page
    this.setState({ skip: previousSkip, limit: previousLimit });
    this.props.listFilterTree(inputSearchText || '', previousSkip, previousLimit);
  };

  // Handle search filter action
  handleSearch = () => {
    let inputSearchText = this.state.inputSearchText.toLowerCase().trim();
    inputSearchText = prettyText(inputSearchText);

    // Having search text, search custom filter for page 1
    if (inputSearchText) {
      // Set limit per page
      this.setState({ skip: 0, limit: NUMBER_FILTER_TREE_PER_PAGE });

      this.props.searchFilterTree(inputSearchText, 0, NUMBER_FILTER_TREE_PER_PAGE);
    } else {
      // Search text is empty, get all filter tree for page 1
      const skip = 0,
        limit = NUMBER_DEFAULT_FILTER + NUMBER_FILTER_TREE_PER_PAGE;

      // Set skip, limit for page 1
      this.setState({ skip, limit });

      // Get filter trees
      this.props.listFilterTree('', skip, limit);
    }
  };

  // Handle change status action
  handleChangeStatus = filterId => {
    const { filterTreeStatus } = this.state;
    const { changeFilterTreeStatus } = this.props;
    filterTreeStatus[filterId] = !filterTreeStatus[filterId];
    this.setState({ filterTreeStatus, firstPageLoading: false });

    // Send request
    const status = filterTreeStatus[filterId] ? 'active' : 'disabled';
    changeFilterTreeStatus(filterId, status);
  };

  // Handle clone filter tree action
  handleClone = async (filterId, cloneTimes, type) => {
    const {
      defaultFilterTrees,
      searchFilterTrees,
      numberOfFilterOptions = LIMIT_FILTER_OPTIONS,
      numberOfFilterOptionsSearch = LIMIT_FILTER_OPTIONS
    } = this.props;
    const { customFilterTrees } = this.state;

    // Get filter tree position
    let clonePosition = _.findIndex(customFilterTrees, { filterId });

    // Check limit filter option
    const filterTree =
      type === 'default'
        ? _.find(defaultFilterTrees, { filterId })
        : type === 'search'
        ? _.find(searchFilterTrees, { filterId })
        : _.find(customFilterTrees, { filterId });
    const { filterOptions = [] } = filterTree;

    const limitNumber = type === 'search' ? numberOfFilterOptionsSearch : numberOfFilterOptions;

    if (filterOptions.length > limitNumber) {
      this.setState({ limitCloneActiveModal: true, limitNumber });
      return;
    }

    // Insert the loading skeleton to list of custom filter trees
    for (let i = 0; i < cloneTimes; i++) {
      customFilterTrees.splice(clonePosition + 1, 0, {});
    }

    this.setState({ clonePosition, cloneTimes, customFilterTrees });
    await this.props.cloneFilterTree(filterId, cloneTimes, filterTree);
    await this.props.getChecklist();
    // Tracking event
    mixpanelV3Helper.trackEventData('Duplicate Filter Tree', {
      feature: 'Manage Filter Tree',
      noDuplicate: cloneTimes
    });
  };

  // Handle remove filter tree action
  handleChangeModal = filterId => {
    this.selectedFilterId = filterId;
    this.setState(({ activeModal }) => ({ activeModal: !activeModal }));
  };

  handleChangeLimitCloneModal = () => {
    this.setState(({ limitCloneActiveModal }) => ({
      limitCloneActiveModal: !limitCloneActiveModal
    }));
  };
  // Handle remove filter option after receiving OK message from confirmation modal
  handleRemove = () => {
    // Delete filter tree
    this.props.removeFilterTree(this.selectedFilterId);
    // Tracking event
    const removeFilterTree = this.state.customFilterTrees?.find(
      e => e.filterId === this.selectedFilterId
    );
    mixpanelV3Helper.trackEventData('Delete Filter Tree', {
      feature: 'Manage Filter Tree',
      optionTypes: removeFilterTree?.filterOptions?.map(e => e.filterType),
      noFilterOptions: removeFilterTree?.filterOptions?.length,
      collections: removeFilterTree?.collections?.map(e => e.title)
    });
    // Init selected it
    this.selectedFilterId = '';
  };

  // Show info banner
  showInfoBanner = () => {
    let dismiss = false;
    try {
      dismiss = localStorage.getItem(`${getShop()}-dismissFilterTreeBanner`);
    } catch (error) {}

    // If the banner is dismissed, hide it
    if (dismiss === 'true') {
      this.setState(({ activeInfoBanner }) => ({ activeInfoBanner: !activeInfoBanner }));
    }
  };

  // Handle dismiss info banner
  handleDismissInfoBanner = () => {
    try {
      localStorage.setItem(`${getShop()}-dismissFilterTreeBanner`, 'true');
    } catch (error) {}

    this.setState(({ activeInfoBanner }) => ({ activeInfoBanner: !activeInfoBanner }));
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  listFilterTree: (q, skip, limit) =>
    dispatch(managementActions.listFilterTree(q, skip, limit, ownProps.i18n)),
  searchFilterTree: (q, skip, limit) =>
    dispatch(managementActions.searchFilterTree(q, skip, limit, ownProps.i18n)),
  changeFilterTreeStatus: (filterId, status) =>
    dispatch(managementActions.changeFilterTreeStatus(filterId, status, ownProps.i18n)),
  cloneFilterTree: (filterId, cloneTimes, filterTree) =>
    dispatch(managementActions.cloneFilterTree(filterId, cloneTimes, filterTree, ownProps.i18n)),
  removeFilterTree: filterId =>
    dispatch(managementActions.removeFilterTree(filterId, ownProps.i18n)),
  getChecklist: () => dispatch(checklistActions.getChecklist(ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  let numberOfFilterOptions = _.get(
    state.oauth.tenantApp,
    'numberOfFilterOptions',
    LIMIT_FILTER_OPTIONS
  );

  let numberOfFilterOptionsSearch = _.get(
    state.oauth.tenantApp,
    'numberOfFilterOptionsSearch',
    LIMIT_FILTER_OPTIONS
  );

  return {
    numberOfFilterOptions,
    numberOfFilterOptionsSearch,
    loading: _.get(state.managementFilterTree, 'loading', false),
    searching: _.get(state.managementFilterTree, 'searching', false),
    cloning: _.get(state.managementFilterTree, 'cloning', false),
    removing: _.get(state.managementFilterTree, 'removing', false),
    removedFilterId: _.get(state.managementFilterTree, 'removedFilterId', false),
    updatingStatus: _.get(state.managementFilterTree, 'updatingStatus', false),
    success: _.get(state.managementFilterTree, 'success', false),
    defaultFilterTrees: _.get(state.managementFilterTree, 'defaultFilterTrees', []),
    searchFilterTrees: _.get(state.managementFilterTree, 'searchFilterTrees', []),
    customFilterTrees: _.get(state.managementFilterTree, 'customFilterTrees', []),
    clonedFilterTrees: _.get(state.managementFilterTree, 'clonedFilterTrees', []),
    total: _.get(state.managementFilterTree, 'total', 0)
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(ManageFilterTree));
